import { computed } from 'vue'
import { readGraphicsUrlConfig, type ExampleGraphicsBackend } from '../utils/graphicsUrlConfig'
import type { ExamplePresetKey } from '../examples/catalog'
import { describePresetKey } from '../examples/graphicsProbe'
import type {
  GraphicsBlockerLike,
  GraphicsInitPhase,
  GraphicsRuntimeError,
  GraphicsRequirementSubjectLike,
} from '../composables/useGraphicsBackendState'

export type { GraphicsBlockerLike, GraphicsInitPhase, GraphicsRuntimeError }

import { useGraphicsBackendState } from '../composables/useGraphicsBackendState'

export interface UseExampleGraphicsCardOptions {
  presetKey: ExamplePresetKey
  extras?: readonly string[]
  /** 该 route 是否被锁定为单一后端（例如纯算法验证页）；锁定时不提供切换。 */
  lockedReason?: string
  /** 锁定页面的固定后端，用于让卡片展示真实条件。 */
  lockedBackend?: ExampleGraphicsBackend
  rebuild?: (backend: ExampleGraphicsBackend) => void
}

/**
 * 控制卡的状态来源：把 catalog 元数据 + 引擎静态预检 + 页面运行时状态合成一份视图模型。
 *
 * 这里只读声明、不创建 renderer/device；页面在引擎创建完成后调用 markReady/markError 回填。
 */
export function useExampleGraphicsCard(options: UseExampleGraphicsCardOptions) {
  const requestedFromUrl = readGraphicsUrlConfig()
  const state = useGraphicsBackendState({
    resolveSubjects: async (): Promise<readonly GraphicsRequirementSubjectLike[]> => {
      const { describeExampleRouteGraphics } = await import('../examples/graphicsProbe')
      const description = await describeExampleRouteGraphics(options.presetKey, options.extras ?? [])
      const plugins = await import('../examples/graphicsProbe').then((module) =>
        module.resolveExamplePresetPlugins(options.presetKey, options.extras ?? [])
      )
      return plugins.map((plugin, index) => ({
        subject: plugin as unknown as GraphicsRequirementSubjectLike['subject'],
        identity: {
          type: 'plugin',
          name: description.subjects[index]?.name ?? plugin.name,
        },
      }))
    },
    ...(options.rebuild ? { rebuild: options.rebuild } : {}),
    ...(options.lockedBackend ? { lockedBackend: options.lockedBackend } : {}),
  })

  const presetLabel = computed(() => describePresetKey(options.presetKey))
  const canSwitchBackend = computed(() => !options.lockedReason)
  const requestedLabel = computed(() => state.requestedBackend.value)
  const actualLabel = computed(() => state.actualBackend.value ?? '尚未创建')
  const confirmedBlockers = computed(() => state.confirmedBlockers.value as readonly GraphicsBlockerLike[])
  const runtimeConfirmations = computed(() =>
    state.needsRuntimeConfirmation.value as readonly GraphicsBlockerLike[]
  )

  return {
    requestedFromUrl,
    presetLabel,
    canSwitchBackend,
    requestedLabel,
    actualLabel,
    confirmedBlockers,
    runtimeConfirmations,
    runtimeError: state.runtimeError,
    phase: state.phase,
    requestedBackend: state.requestedBackend,
    actualBackend: state.actualBackend,
    blockers: state.blockers,
    selectBackend: state.selectBackend,
    rebuild: state.rebuild,
    runPreflight: state.runPreflight,
    markCreating: state.markCreating,
    markReady: state.markReady,
    markError: state.markError,
  }
}
